import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import {
  verifyAdmin,
  listRsvps,
  listTrashedRsvps,
  getRsvpStats,
  downloadRsvpXlsx,
  softDeleteRsvp,
  restoreRsvp,
  permanentDeleteRsvp,
} from "../lib/api";
import {
  LogOut,
  Loader2,
  Download,
  Trash2,
  RotateCcw,
  XCircle,
} from "lucide-react";

export default function AdminPage() {
  const { t, lang, setLang } = useLang();
  const [token, setToken] = useState(
    () => sessionStorage.getItem("admin_token") || ""
  );
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [rsvps, setRsvps] = useState([]);
  const [trashed, setTrashed] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState("list"); // "list" | "trash"

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadRsvpXlsx(token);
    } catch (e) {
      setError(e?.response?.data?.detail || "Export error");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const check = async () => {
      if (!token) return;
      try {
        await verifyAdmin(token);
        setAuthed(true);
      } catch {
        setAuthed(false);
        setToken("");
        sessionStorage.removeItem("admin_token");
      }
    };
    check();
  }, [token]);

  const reload = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const [list, trash, s] = await Promise.all([
        listRsvps(token),
        listTrashedRsvps(token),
        getRsvpStats(token),
      ]);
      setRsvps(list);
      setTrashed(trash);
      setStats(s);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }, [authed, token]);

  useEffect(() => {
    reload();
  }, [reload]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await verifyAdmin(input);
      sessionStorage.setItem("admin_token", input);
      setToken(input);
      setAuthed(true);
    } catch {
      setError(t.admin.wrong);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("admin_token");
    setToken("");
    setAuthed(false);
    setInput("");
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm(t.admin.actions.confirmDelete)) return;
    try {
      await softDeleteRsvp(token, id);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete error");
    }
  };

  const handleRestore = async (id) => {
    try {
      await restoreRsvp(token, id);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail || "Restore error");
    }
  };

  const handlePermanentDelete = async (id) => {
    if (!window.confirm(t.admin.actions.confirmPermanentDelete)) return;
    try {
      await permanentDeleteRsvp(token, id);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete error");
    }
  };

  if (!authed) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6 paper-texture"
        data-testid="admin-login-page"
      >
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          onSubmit={login}
          className="w-full max-w-md bg-ivory border border-wedding-border p-10 md:p-14 shadow-[0_20px_40px_rgba(26,21,20,0.08)]"
        >
          <div className="flex items-center justify-between mb-8">
            <p className="font-script text-3xl text-burgundy">N & R</p>
            <div className="flex items-center text-xs tracking-[0.2em] border border-wedding-border rounded-full overflow-hidden">
              {[
                { code: "ro", label: "RO" },
                { code: "it", label: "IT" },
                { code: "de", label: "DE" },
              ].map(({ code, label }) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setLang(code)}
                  className={`px-2 py-1 transition-colors ${
                    lang === code ? "bg-burgundy text-ivory" : "text-ink/60"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <h1 className="font-serif font-light text-3xl md:text-4xl text-ink mb-2">
            {t.admin.title}
          </h1>
          <p className="font-serif italic text-ink/60 mb-8">
            {t.admin.subtitle}
          </p>
          <label className="text-xs uppercase tracking-[0.3em] text-warm-brown mb-2 block">
            {t.admin.tokenLabel}
          </label>
          <input
            type="password"
            className="underline-input mb-6"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            data-testid="admin-token-input"
            autoFocus
          />
          {error && (
            <p
              className="text-sm text-burgundy font-serif italic mb-4"
              data-testid="admin-error"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-burgundy text-ivory rounded-full text-xs uppercase tracking-[0.3em] hover:bg-wine transition-colors"
            data-testid="admin-login-button"
          >
            {t.admin.enter}
          </button>
        </motion.form>
      </div>
    );
  }

  const isTrash = view === "trash";
  const rows = isTrash ? trashed : rsvps;

  return (
    <div
      className="min-h-screen paper-texture p-6 md:p-10"
      data-testid="admin-dashboard"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="font-script text-4xl text-burgundy leading-none">
              N & R
            </p>
            <p className="text-xs uppercase tracking-[0.3em] text-warm-brown mt-1">
              {t.admin.title}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center text-xs tracking-[0.2em] border border-wedding-border rounded-full overflow-hidden">
              {[
                { code: "ro", label: "RO" },
                { code: "it", label: "IT" },
                { code: "de", label: "DE" },
              ].map(({ code, label }) => (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  className={`px-3 py-1.5 transition-colors ${
                    lang === code ? "bg-burgundy text-ivory" : "text-ink/60"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-5 py-2 bg-burgundy text-ivory rounded-full text-xs uppercase tracking-[0.3em] hover:bg-wine transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="admin-export-button"
            >
              {exporting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              Excel
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-5 py-2 border border-wedding-border rounded-full text-xs uppercase tracking-[0.3em] text-ink/70 hover:border-burgundy hover:text-burgundy transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut size={14} />
              {t.admin.logout}
            </button>
          </div>
        </div>

        {/* View tabs */}
        <div className="flex items-center gap-2 mb-6" data-testid="admin-view-tabs">
          <button
            onClick={() => setView("list")}
            className={`px-5 py-2 rounded-full text-xs uppercase tracking-[0.3em] border transition-colors ${
              !isTrash
                ? "bg-burgundy text-ivory border-burgundy"
                : "border-wedding-border text-ink/60 hover:border-burgundy hover:text-burgundy"
            }`}
            data-testid="admin-tab-list"
          >
            {t.admin.view.confirmations} ({rsvps.length})
          </button>
          <button
            onClick={() => setView("trash")}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs uppercase tracking-[0.3em] border transition-colors ${
              isTrash
                ? "bg-burgundy text-ivory border-burgundy"
                : "border-wedding-border text-ink/60 hover:border-burgundy hover:text-burgundy"
            }`}
            data-testid="admin-tab-trash"
          >
            <Trash2 size={14} />
            {t.admin.view.trash} ({trashed.length})
          </button>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 size={32} className="text-burgundy animate-spin" />
          </div>
        )}

        {stats && !loading && !isTrash && (
          <div
            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4 mb-10"
            data-testid="admin-stats"
          >
            <StatCard label={t.admin.stats.total} value={stats.total_submissions} testid="stat-total" />
            <StatCard label={t.admin.stats.attending} value={stats.attending} testid="stat-attending" highlight />
            <StatCard label={t.admin.stats.notAttending} value={stats.not_attending} testid="stat-notattending" />
            <StatCard label={t.admin.stats.adults} value={stats.total_adults} testid="stat-adults" />
            <StatCard label={t.admin.stats.children} value={stats.total_children} testid="stat-children" />
            <StatCard label={t.admin.stats.accommodation} value={stats.need_accommodation} testid="stat-accommodation" />
            <StatCard label={t.admin.stats.vegan} value={stats.menu_vegan} testid="stat-vegan" />
            <StatCard label={t.admin.stats.vegetarian} value={stats.menu_vegetarian} testid="stat-vegetarian" />
          </div>
        )}

        {!loading && (
          <div className="bg-ivory border border-wedding-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="rsvp-table">
                <thead className="bg-cream/60 text-left">
                  <tr className="text-xs uppercase tracking-[0.2em] text-warm-brown">
                    <th className="px-4 py-4">{t.admin.table.name}</th>
                    <th className="px-4 py-4">{t.admin.table.email}</th>
                    <th className="px-4 py-4">{t.admin.table.phone}</th>
                    <th className="px-4 py-4">{t.admin.table.attending}</th>
                    <th className="px-4 py-4">{t.admin.table.adults}</th>
                    <th className="px-4 py-4">{t.admin.table.children}</th>
                    <th className="px-4 py-4">{t.admin.table.accommodation}</th>
                    <th className="px-4 py-4">{t.admin.table.song}</th>
                    <th className="px-4 py-4">
                      {isTrash ? t.admin.deletedAt : t.admin.table.date}
                    </th>
                    <th className="px-4 py-4 text-right">
                      {t.admin.table.actions}
                    </th>
                  </tr>
                </thead>
                <tbody className="font-serif">
                  {rows.map((r) => {
                    const firstAdult = r.adults?.[0];
                    const name = firstAdult
                      ? `${firstAdult.first_name} ${firstAdult.last_name}`
                      : "—";
                    const fmtPeople = (people) =>
                      (people || [])
                        .map(
                          (p) =>
                            `${p.first_name} ${p.last_name} (${p.menu}${
                              p.allergies ? ` · ${p.allergies}` : ""
                            })`
                        )
                        .join("\n");
                    const dateStr = isTrash
                      ? r.deleted_at
                        ? new Date(r.deleted_at).toLocaleString()
                        : "—"
                      : new Date(r.submitted_at).toLocaleString();
                    return (
                      <tr
                        key={r.id}
                        className="border-t border-wedding-border/50 hover:bg-cream/30 align-top"
                        data-testid={`rsvp-row-${r.id}`}
                      >
                        <td className="px-4 py-4 text-ink whitespace-nowrap">
                          {name}
                        </td>
                        <td className="px-4 py-4 text-ink/80">{r.email}</td>
                        <td className="px-4 py-4 text-ink/80">{r.phone}</td>
                        <td className="px-4 py-4">
                          <span
                            className={`text-xs uppercase tracking-[0.2em] ${
                              r.attending ? "text-burgundy" : "text-ink/40"
                            }`}
                          >
                            {r.attending ? "✓" : "✕"}
                          </span>
                        </td>
                        <td
                          className="px-4 py-4 text-ink/80 text-xs whitespace-pre-line max-w-[260px]"
                          title={fmtPeople(r.adults)}
                        >
                          {r.adults?.length ? fmtPeople(r.adults) : "—"}
                        </td>
                        <td
                          className="px-4 py-4 text-ink/80 text-xs whitespace-pre-line max-w-[220px]"
                          title={fmtPeople(r.children)}
                        >
                          {r.children?.length ? fmtPeople(r.children) : "—"}
                        </td>
                        <td className="px-4 py-4 text-ink/80">
                          {r.needs_accommodation ? "✓" : "—"}
                        </td>
                        <td className="px-4 py-4 text-ink/70 max-w-[180px] truncate">
                          {r.favorite_song || "—"}
                        </td>
                        <td className="px-4 py-4 text-ink/60 text-xs">
                          {dateStr}
                        </td>
                        <td className="px-4 py-4 text-right whitespace-nowrap">
                          {isTrash ? (
                            <div className="inline-flex items-center gap-2">
                              <button
                                onClick={() => handleRestore(r.id)}
                                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-wedding-border text-ink/60 hover:border-emerald-700 hover:text-emerald-700 transition-colors"
                                title={t.admin.actions.restore}
                                aria-label={t.admin.actions.restore}
                                data-testid={`restore-${r.id}`}
                              >
                                <RotateCcw size={15} />
                              </button>
                              <button
                                onClick={() => handlePermanentDelete(r.id)}
                                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-wedding-border text-ink/60 hover:border-burgundy hover:text-burgundy transition-colors"
                                title={t.admin.actions.permanentDelete}
                                aria-label={t.admin.actions.permanentDelete}
                                data-testid={`permanent-delete-${r.id}`}
                              >
                                <XCircle size={15} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleSoftDelete(r.id)}
                              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-wedding-border text-ink/60 hover:border-burgundy hover:text-burgundy transition-colors"
                              title={t.admin.actions.delete}
                              aria-label={t.admin.actions.delete}
                              data-testid={`delete-${r.id}`}
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-4 py-16 text-center font-serif italic text-ink/50"
                      >
                        {isTrash ? t.admin.trashEmpty : "—"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, testid, highlight }) {
  return (
    <div
      className={`p-4 border text-center ${
        highlight
          ? "border-burgundy bg-burgundy/5"
          : "border-wedding-border bg-ivory"
      }`}
      data-testid={testid}
    >
      <p
        className={`font-serif font-light text-3xl md:text-4xl ${
          highlight ? "text-burgundy" : "text-ink"
        }`}
      >
        {value}
      </p>
      <p className="text-[10px] uppercase tracking-[0.25em] text-warm-brown mt-1">
        {label}
      </p>
    </div>
  );
}
